import {
  BodyLinkV3,
  LayoutGrid,
  TypographyRhythm,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';

import PageLayoutV3 from '@/app/components/PageLayoutV3';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';

import { AccessAndUseSection } from '../_components/AccessAndUseSection';
import { TermsOfDeliverySection } from '../_components/TermsOfDeliverySection';

const Page = (): JSX.Element => {
  const enable2024Terms = useFeatureFlag('enable-2024-terms-of-use');
  const t = useTranslations();

  return (
    <PageLayoutV3
      pageName="Terms and Conditions"
      containerProps={{
        name: 'terms-and-conditions',
        padding: 'both',
        lang: 'fr',
      }}
      footerProps={{
        wrapperClassName: 'bg-white',
      }}
    >
      <LayoutGrid data-cy="terms-and-conditions-fr-page">
        <TypographyRhythm className="col-main">
          <>
            <TypographyV3 Element="h1" variant="h1">
              CONDITIONS D'UTILISATION D'ŌURA
            </TypographyV3>
            <TypographyV3>
              <em>Dernière mise à jour : le 2 avril 2024</em>
            </TypographyV3>
            <TypographyV3>
              Nous vous souhaitons la bienvenue et vous remercions de l'intérêt
              que vous portez à Ōura. Les présentes Conditions d'utilisation
              (l'« Accord ») décrivent les modalités applicables à votre
              utilisation d'ouraring.com (le « Site »), de l'anneau intelligent,
              du chargeur et/ou d'autres dispositifs électroniques (les «
              Produits »), y compris l'application mobile Ōura (l'« Application
              mobile ») (désignés collectivement par le terme « Services »). Le
              Site et l'Application mobile sont détenus et gérés par{' '}
              <strong>Ōura Health Oy</strong>, ses sociétés affiliées et ses
              filiales, telles que Ouraring Inc. (désignées collectivement par
              les termes « Ōura » ou « Oura »).
            </TypographyV3>
            <TypographyV3>
              Dans le présent Accord, nous utilisons, nous concernant, les
              termes « Ōura », « Oura », « nous » ou « nos » ; nous utilisons,
              vous concernant, les termes « vous » ou « Client ». Dans le
              présent Accord, Oura et le Client sont dénommés individuellement
              une « Partie » et collectivement les « Parties ».
            </TypographyV3>
            <TypographyV3>
              En accédant aux Services ou en les utilisant, y compris l'accès au
              Site, vous comprenez et acceptez expressément d'être lié par
              toutes les modalités du présent Accord et de la Politique de
              confidentialité (disponible à l'adresse{' '}
              <BodyLinkV3 color="inherit" href="/privacy-policy">
                ouraring.com/privacy-policy
              </BodyLinkV3>
              ) qui est intégrée aux présentes par référence. Si vous n'acceptez
              pas ces modalités, vous ne pouvez pas utiliser les Services.
            </TypographyV3>

            <AccessAndUseSection />

            <TypographyV3 Element="h2" variant="h3">
              Responsabilités du Client
            </TypographyV3>
            <TypographyV3>
              Vous reconnaissez être seul responsable de l'utilisation que vous
              faites des Services, directement ou indirectement, y compris de la
              compréhension du fait que cet accès ou cette utilisation est
              autorisé par le présent Accord ou en violation de celui-ci. De
              plus, vous êtes seul responsable du respect de toutes les lois
              applicables concernant votre utilisation des Services. En outre,
              vous ne devez utiliser les Services qu'à des fins légales et vous
              devez mener toutes vos activités par le biais des Services
              conformément à toutes les lois et réglementations applicables, y
              compris, mais sans s'y limiter, toutes les lois et réglementations
              fédérales et d'État applicables régissant l'offre et la vente de
              titres, le blanchiment d'argent et la lutte contre le terrorisme.
            </TypographyV3>
            <TypographyV3>
              Vous seul êtes responsable de vous assurer que vous êtes en mesure
              d'accéder aux Services et de les utiliser, y compris en sécurisant
              votre propre matériel compatible, l'Application mobile, l'accès
              Internet, une application mobile de sécurité, les dispositifs ou
              services de sauvegarde, et toute autre exigence, et de maintenir
              cette possibilité d'accès et d'utilisation. Oura n'est pas tenue
              de fournir une application mobile ou un matériel supplémentaire.
              Vous acceptez également qu'Oura décline toute responsabilité en
              cas de perte de données ou de tout autre dommage ou perte subis en
              lien avec votre utilisation des Services, y compris tout
              manquement à fournir des dispositifs ou services de sécurité ou de
              sauvegarde adéquats.
            </TypographyV3>
            <TypographyV3>
              Vous devez vous assurer qu'Oura dispose d'informations exactes et
              à jour pour votre compte client, y compris des coordonnées et des
              informations de paiement à jour. De plus, vous devez consulter
              régulièrement le compte de messagerie associé à votre compte
              client afin de vérifier la présence de communications de la part
              d'Oura.
            </TypographyV3>
            <TypographyV3>
              Si un nom d'utilisateur, un mot de passe, un fichier
              d'identifiants ou toute autre information vous est fourni dans le
              cadre d'une procédure de sécurité (« Identifiants), vous devez
              traiter ces informations de manière confidentielle et vous ne
              devez pas divulguer les Identifiants à toute autre personne ou
              entité. Vous reconnaissez que votre compte et vos Identifiants
              sont réservés à un usage personnel, et vous acceptez en outre de
              ne permettre à aucune autre personne d'accéder aux Services ou à
              certaines parties des Services en utilisant votre nom
              d'utilisateur, votre mot de passe ou d'autres informations de
              sécurité. Vous devez informer immédiatement Oura de tout accès non
              autorisé à vos Identifiants, de toute utilisation non autorisée de
              vos Identifiants, ou de toute autre brèche ou violation de
              sécurité. Oura a le droit de désactiver à tout moment tout nom
              d'utilisateur, mot de passe, fichier d'identifiants ou autre
              identifiant, qu'il ait été choisi par vous ou fourni par Oura.
            </TypographyV3>
            <TypographyV3>
              Oura doit déployer des efforts commercialement raisonnables afin
              de proposer des services d'assistance adéquats pour les Services.
              Nonobstant ce qui précède, le présent Accord ne vous donne droit à
              aucune garantie de niveau, de disponibilité ou de délai de mise en
              œuvre concernant les services d'assistance des Services.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Paiement et frais
            </TypographyV3>
            <TypographyV3>
              Les Services payants comprennent les Services, qui peuvent être
              des achats uniques ou des services d'abonnement à renouvellement
              automatique (les « Services payants »), y compris notre Site et
              notre Application mobile (les « Abonnements »). Nous pouvons
              modifier, suspendre ou interrompre les Services payants à tout
              moment et pour quelque raison que ce soit, et Oura se réserve le
              droit, à sa seule discrétion, de déterminer quels Services ou
              parties de ces Services nécessitent un paiement.
            </TypographyV3>
            <TypographyV3>
              Les Services payants peuvent inclure des Produits précommandés qui
              seront fabriqués pour vous ultérieurement (« Précommande »). Vous
              devrez vous acquitter de frais de Précommande au moment de la
              Précommande. La date réelle d'expédition de toute Précommande
              acceptée dépendra de divers facteurs, y compris, mais sans s'y
              limiter, la date de paiement des frais de Précommande et le
              calendrier de fabrication d'Oura. La date d'expédition des
              Précommandes n'est pas garantie.
            </TypographyV3>
            <TypographyV3>
              Vous acceptez de payer tous les frais applicables aux Services
              payants, y compris, mais sans s'y limiter, les frais d'abonnement
              mensuels, les frais d'utilisation et les frais d'offre, ainsi que
              tous les autres frais ou coûts que vous acceptez de payer dans le
              cadre des Services payants au cours du processus de règlement (les
              « Frais »). Vous acceptez de payer tous les Frais et toutes les
              taxes applicables encourus avant la résiliation ou l'annulation de
              l'Accord.
            </TypographyV3>
            <TypographyV3>
              Vous autorisez Oura à débiter votre mode de paiement désigné au
              titre des Services payants. En indiquant un mode de paiement
              conforme, vous déclarez et garantissez que vous êtes autorisé à
              utiliser le mode de paiement désigné et que vous nous autorisez,
              nous ou notre société de traitement des paiements tierce, à
              débiter votre mode de paiement du montant total de votre achat, y
              compris les taxes et autres frais applicables. Si le mode de
              paiement ne peut pas être vérifié, s'il n'est pas valide ou pas
              conforme sous d'autres aspects, votre Service payant peut être
              suspendu ou annulé. Vous devez résoudre tout problème que nous
              rencontrons par rapport au mode de paiement que vous indiquez afin
              de continuer à utiliser le Service. Si vous acceptez une offre
              promotionnelle ou modifiez vos Services payants, les Frais, taxes
              et montants facturés sont susceptibles de varier. Les montants
              facturés peuvent également varier en cas de modification des taxes
              ou des taux de change applicables. Vous nous autorisez, nous ou
              notre société de traitement des paiements tierce, à débiter votre
              mode de paiement du montant correspondant. Aucun remboursement ne
              sera effectué, sauf si la loi l'exige. Les remises individuelles
              ne peuvent être combinées ou cumulées avec d'autres remises. Cette
              obligation de paiement restera en vigueur après la résiliation ou
              l'annulation du présent Accord pour quelque raison que ce soit.
            </TypographyV3>
            <TypographyV3>
              Si vous choisissez de financer un achat par l'intermédiaire de
              notre société de traitement des paiements tierce et que la date
              d'expédition d'un ou de plusieurs articles de votre commande est
              repoussée, le ou les paiements de votre prêt, y compris les
              intérêts, peuvent être dus avant que nous expédiions tous les
              articles. Veuillez noter que vous ne pouvez pas bénéficier d'un
              remboursement des intérêts qui ont déjà couru sur un montant qui
              est remboursé ultérieurement.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Abonnements
            </TypographyV3>
            <TypographyV3>
              Certains Services payants sont des achats par abonnement, auxquels
              s'appliquent les modalités suivantes :
            </TypographyV3>
            <TypographyV3>
              <strong>
                Votre période d'Abonnement peut être permanente, mensuelle ou
                annuelle (« Période(s) d'abonnement »), comme décrite lors de
                l'achat des Services payants. Votre Abonnement sera renouvelé
                automatiquement pour des Périodes d'abonnement supplémentaires
                jusqu'à ce que vous le résiliiez, ou jusqu'à ce qu'Oura le
                suspende ou le résilie. Sauf indication contraire de notre part,
                votre mode de paiement désigné sera débité avant ou au début de
                chaque Période d'abonnement des frais d'abonnement, ainsi que
                des taxes et autres frais applicables. Avant de vous facturer
                une Période d'abonnement, nous vous informerons des frais
                applicables, et le renouvellement se fera au prix alors en
                vigueur pour le Service payant.
              </strong>
            </TypographyV3>
            <TypographyV3>
              <strong>
                Vous pouvez résilier votre Abonnement à tout moment. Votre
                résiliation prendra effet à la fin de la Période d'abonnement en
                cours. Pour résilier votre abonnement et le paiement
                automatique, cliquez sur le bouton « Cancel Account » (Résilier
                mon compte) dans votre compte ou envoyez-nous un e-mail à
                l'adresse support@ouraring.com. La résiliation ne vous donne
                droit à aucun remboursement des Frais payés précédemment et vous
                ne bénéficierez pas d'un remboursement au prorata pour le reste
                de la Période d'abonnement. Dans le cas où vous résiliez votre
                Abonnement, notez que nous sommes encore susceptibles de vous
                envoyer des communications promotionnelles, à moins que vous ne
                choisissiez de ne plus en recevoir en suivant la procédure de
                désabonnement indiquée dans les communications.
              </strong>
            </TypographyV3>
            <TypographyV3>
              <strong>
                Quand vous résiliez un Abonnement, vous résiliez uniquement les
                futurs frais de votre Abonnement. Vous ne bénéficiez d'aucun
                remboursement pour la Période d'abonnement en cours déjà payée,
                mais vous conservez l'accès complet à cet Abonnement jusqu'à la
                fin de ladite Période d'abonnement en cours.
              </strong>{' '}
              À tout moment et pour quelque raison que ce soit, nous pouvons
              accorder un remboursement, une remise ou une autre contrepartie («
              avoirs ») à certains ou à tous nos utilisateurs. Le montant et la
              nature de ces avoirs, ainsi que la décision de les accorder
              relèvent de notre seule et entière discrétion. Le fait que vous
              bénéficiiez d'avoirs dans une situation donnée ne vous donne pas
              droit à des avoirs à l'avenir pour des situations similaires ni ne
              nous oblige à proposer des avoirs à l'avenir.
            </TypographyV3>
            <TypographyV3>
              Si vous ne résidez pas aux États-Unis et que vous changez d'avis
              concernant votre achat, vous pouvez prétendre à un remboursement
              total dans un délai de quatorze (14) jours (le « Délai de
              réflexion »), à condition que vous ne vous soyez pas connecté aux
              Services, que vous n'ayez pas utilisé les Services d'une autre
              manière ou que vous n'ayez pas commencé à utiliser les Services en
              tant qu'abonné pendant le Délai de réflexion.
            </TypographyV3>
            <TypographyV3>
              De temps en temps, nous sommes susceptibles de vous proposer
              d'essayer gratuitement, sans paiement, certains Abonnements pour
              des périodes spécifiques.{' '}
              <strong>
                Avant le début de votre essai gratuit, nous vous informerons des
                Frais d'abonnement applicables qui vous seront facturés à
                l'expiration de l'essai gratuit. À moins que vous ne résiliiez
                votre Abonnement avant la fin de l'essai gratuit en suivant la
                procédure décrite ci-dessus, à la fin de l'essai gratuit, nous
                ou notre société de traitement des paiements tierce débiterons
                votre mode de paiement désigné de façon récurrente de vos Frais
                d'abonnement, ainsi que de tous les autres frais et taxes
                applicables, tant que vous maintiendrez votre Abonnement. Vous
                devez résilier votre Abonnement avant la fin de la période
                d'essai si vous ne souhaitez pas qu'il vous soit facturé. La
                procédure de résiliation de l'Abonnement est décrite ci-dessus.{' '}
              </strong>
            </TypographyV3>
            <TypographyV3>
              Vos informations de paiement seront traitées et stockées par une
              société de traitement des paiements tierce. Tous les titulaires de
              comptes payants doivent disposer d'au moins un mode de paiement
              valide pour le paiement des Frais, qui sont décrits plus en détail
              lors du règlement. Tous les Frais sont calculés et facturés de
              manière mensuelle ou annuelle, selon votre choix, sont dus dès
              réception et peuvent être modifiés. Vous reconnaissez que les
              Frais sont facturés de manière périodique et vous acceptez la
              responsabilité de tous les frais récurrents avant la résiliation.
              Les frais seront facturés ou débités du mode de paiement désigné
              et enregistré par vos soins un jour avant l'anniversaire mensuel
              ou annuel de la date d'achat initiale.
            </TypographyV3>
            <TypographyV3>
              Si vous ne vous êtes pas connecté aux Services ou que vous n'avez
              pas utilisé les Services pendant une période minimale de six (6)
              mois suivant l'achat ou la réception d'un Produit durant l'essai
              gratuit, nous nous réservons le droit de résilier votre
              abonnement, d'annuler tout achat en attente et de vous rembourser
              le prix d'achat du Produit uniquement. Vous ne pourrez pas
              bénéficier d'un remboursement de la valeur de l'Abonnement durant
              l'essai gratuit.
            </TypographyV3>
            <TypographyV3>
              Oura se réserve le droit d'ajuster à tout moment les Frais de nos
              Services payants, ou toute caractéristique ou partie de nos
              Services payants. Vous reconnaissez qu'Oura peut modifier les
              Frais des Services payants à tout moment. En pareil cas, Oura vous
              en informera par le biais de l'adresse e-mail associée à votre
              compte au moins trente (30) jours avant la date d'entrée en
              vigueur de la modification. En continuant à utiliser les Services,
              vous acceptez les modifications apportées aux Frais. Vous êtes
              tenu d'acquitter toutes les taxes applicables et les taxes vous
              seront facturées lorsque la loi l'exige.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Données et communications
            </TypographyV3>
            <TypographyV3>
              Oura peut collecter et traiter des informations sur votre
              utilisation des Services. Vous consentez à la collecte et à
              l'utilisation de ces informations par Oura, ainsi qu'au partage de
              ces informations avec des fournisseurs de services tiers dans le
              but de fournir, de commercialiser et d'améliorer les Services, et
              pour toute autre raison décrite dans la Politique de
              confidentialité. Toutes les informations personnelles collectées
              par Oura sont traitées conformément à la
              <BodyLinkV3 color="inherit" href="/privacy-policy">
                Politique de confidentialité
              </BodyLinkV3>
              .
            </TypographyV3>
            <TypographyV3>
              <strong>
                En acceptant les modalités du présent Accord et en fournissant
                vos coordonnées à Oura, vous autorisez expressément Oura, ses
                sociétés affiliées et ses agents à vous contacter de temps en
                temps par le biais des adresses postales, numéros de téléphone
                ou adresses e-mail que vous fournissez à Oura.
              </strong>{' '}
              Par cette autorisation, vous acceptez d'être contacté par Oura et
              ses fournisseurs de services par téléphone, e-mail, SMS ou un
              autre moyen, à quelque fin que ce soit, y compris, mais sans s'y
              limiter, les notifications relatives aux Services et à votre
              compte, aux abonnements, aux achats, aux mises à niveau
              disponibles, aux problèmes de facturation et de traitement des
              paiements, et aux communications de télémarketing. Ces
              communications autorisées peuvent inclure l'utilisation d'une
              technologie de numérotation automatique ou l'utilisation de
              messages préenregistrés. Vous êtes redevable de tous les frais qui
              peuvent vous être facturés par votre ou vos fournisseurs de
              services quand nous vous contactons. Vous reconnaissez également
              que votre consentement à ce qui précède ne conditionne pas votre
              utilisation des Services Oura, et si vous ne souhaitez pas
              accorder votre consentement, vous pouvez nous contacter et
              demander à être placé sur une liste d'interruption de
              sollicitation, ou vous pouvez vous désabonner à tout moment en
              utilisant le système de désabonnement indiqué dans ces
              communications.
            </TypographyV3>
            <TypographyV3>
              Oura décline toute responsabilité au titre du présent Accord pour
              toutes les informations que vous fournissez à Oura et qui
              pourraient constituer des dossiers médicaux électroniques de
              patients ou pour toutes les informations similaires fournies par
              vous ou par un utilisateur final, nonobstant toute disposition
              contraire dans le présent Accord ou au titre d'autres exigences
              prévues par toute loi, règle ou réglementation fédérale, étatique
              ou internationale applicable.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Droits de propriété intellectuelle
            </TypographyV3>
            <TypographyV3>
              ŌURA, Ō et OURA sont des marques d'Ōura Health Oy. Les Contenus
              Oura, les Produits Oura, les fonctionnalités et les Services Oura,
              et notre technologie sous-jacente sont protégés par les lois sur
              les droits d'auteur, les marques, les brevets et la propriété
              intellectuelle, ainsi que par d'autres lois des États-Unis et de
              pays étrangers. Tous droits réservés. Il ne vous est accordé,
              implicitement ou autrement, aucune licence ni aucun droit
              d'utilisation des marques apparaissant sur les Services, ou
              utilisées ou affichées en relation avec ces derniers (les «
              Marques »). Les Services peuvent également contenir, ou à faire
              référence à, des marques, des noms commerciaux, des noms de
              produits et des logos tiers, qui peuvent être des marques déposées
              appartenant à leurs propriétaires respectifs. Vous ne pouvez en
              aucun cas utiliser ou copier les Marques. Rien dans les présentes
              ne doit être interprété comme l'octroi d'une licence ou d'un droit
              d'utilisation des Marques affichées en relation avec les Services
              sans l'autorisation écrite expresse d'Oura.
            </TypographyV3>
            <TypographyV3>
              Tous les contenus fournis en association avec les Services et le
              présent Accord, y compris, mais sans s'y limiter, le Site, le
              Produit, l'Application mobile, tous les textes, graphiques,
              interfaces utilisateur, interfaces visuelles, photographies,
              images/vidéos, arts électroniques, sons/contenus audio, données,
              programmes de communication, codes exécutables, codes
              informatiques et données (collectivement, le « Contenu »)
              formatés, organisés et collectés sous diverses formes, y compris
              la conception, l'organisation, la sélection, la coordination,
              l'expression, l'apparence, l'arrangement, la mise en page, les
              pages, les écrans et les bases de données de ce Contenu, contenus
              dans le Contenu, les Services et la technologie sous-jacente,
              ainsi que toute autre œuvre protégée par des droits d'auteur
              associée aux Services (les « Œuvres protégées par des droits
              d'auteur »), sont exclusivement détenus, contrôlés ou concédés
              sous licence par ou à Oura, et sont protégés par les lois
              étasuniennes et internationales sur les droits d'auteur. Vous
              acceptez de ne pas copier, reproduire, modifier, distribuer ou
              afficher publiquement les Œuvres protégées par des droits d'auteur
              ni d'en créer des œuvres dérivées, directement ou indirectement,
              sans l'autorisation écrite expresse préalable d'Oura.
            </TypographyV3>
            <TypographyV3>
              Si vous fournissez des communications ou des documents à Oura par
              courrier, e-mail, téléphone ou un autre moyen, suggérant ou
              recommandant des changements aux Services, y compris, mais sans
              s'y limiter, de nouvelles caractéristiques ou fonctionnalités s'y
              rapportant, ou des commentaires, des questions, des suggestions ou
              autre (« Commentaires »), Oura peut utiliser ces Commentaires
              indépendamment de toute autre obligation ou limitation entre les
              Parties régissant ces Commentaires. Oura peut utiliser, sans
              rétribution ni compensation à l'égard d'une partie quelconque,
              toute idée, tout savoir-faire, tout concept, toute technique ou
              tout autre droit de propriété intellectuelle contenu dans les
              Commentaires, à quelque fin que ce soit, bien qu'Oura ne soit pas
              tenue d'utiliser les Commentaires.
            </TypographyV3>
            <TypographyV3>
              Oura respecte les droits de propriété intellectuelle d'autrui, et
              s'engage à traiter et à examiner sans délai les notifications pour
              atteinte supposée aux droits d'auteur ou à d'autres lois
              applicables concernant la propriété intellectuelle. Toute
              notification d'une violation supposée doit être envoyée à l'Agent
              désigné d'Oura à l'adresse ip@ouraring.com et doit contenir tous
              les éléments suivants : (i) une signature (physique ou
              électronique) du titulaire des droits d'auteur ou d'une personne
              autorisée à agir au nom du titulaire des droits d'auteur ; (ii)
              une description de l'œuvre protégée par des droits d'auteur qui,
              selon vous, fait l'objet d'une atteinte aux droits d'auteur ;
              (iii) une description du contenu qui, selon vous, porte atteinte à
              l'œuvre mentionnée ci-dessus et qui doit être supprimé ou dont
              l'accès doit être désactivé, ainsi que suffisamment d'informations
              pour permettre aux administrateurs d'Oura de trouver ledit contenu
              ; (iv) suffisamment d'informations pour nous permettre de vous
              contacter, telles que votre adresse, votre numéro de téléphone et
              votre adresse e-mail ; (v) une déclaration selon laquelle vous
              pensez en toute bonne foi que l'utilisation dudit contenu de la
              manière dont vous vous plaignez n'est pas autorisée par le
              titulaire des droits d'auteur, son agent ou la loi ; et (vi) une
              déclaration indiquant que les informations contenues dans la
              notification sont exactes et dans laquelle vous déclarez sur
              l'honneur que vous êtes le titulaire des droits d'auteur ou que
              vous êtes autorisé à agir au nom du titulaire des droits d'auteur
              auxquels vous estimez qu'il a été porté atteinte.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Exclusion de garantie
            </TypographyV3>
            <TypographyV3>
              LES SERVICES VOUS SONT FOURNIS « TELS QUELS, AVEC TOUTES LEURS
              IMPERFECTIONS » ET « TELS QUE DISPONIBLES » SANS GARANTIE D'AUCUNE
              SORTE ; OURA ET SES FILIALES, SOCIÉTÉS AFFILIÉES, DIRIGEANTS,
              ADMINISTRATEURS, EMPLOYÉS, REPRÉSENTANTS, AGENTS, PARTENAIRES ET
              CONCÉDANTS DE LICENCE EXCLUENT PAR LA PRÉSENTE TOUTES LES
              GARANTIES ET CONDITIONS RELATIVES AUX SERVICES, QU'ELLES SOIENT
              EXPRESSES OU IMPLICITES, ET EXCLUENT EXPRESSÉMENT TOUTE GARANTIE
              IMPLICITE DE TITRE, DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN USAGE
              PARTICULIER, DE QUALITÉ SATISFAISANTE ET DE NON-VIOLATION. OURA NE
              GARANTIT PAS QU'AUCUNE INTERFÉRENCE N'AURA LIEU AVEC VOTRE
              UTILISATION DES SERVICES, LA DISPONIBILITÉ DU CONTENU, QUE LES
              FONCTIONS CONTENUES DANS LES SERVICES RÉPONDRONT À VOS BESOINS,
              QUE LES SERVICES SERONT EXEMPTS DE VIRUS OU D'AUTRES ÉLÉMENTS
              NUISIBLES, QUE LE FONCTIONNEMENT DES SERVICES SERA ININTERROMPU OU
              EXEMPT D'ERREURS, QUE LES DÉFAUTS DES SERVICES SERONT CORRIGÉS OU
              QUE LES FONCTIONS CONTENUES DANS LES SERVICES FONCTIONNERONT AVEC
              D'AUTRES APPLICATIONS OU ÉQUIPEMENTS MOBILES OU AU SEIN D'UN
              SYSTÈME. AUCUNE INFORMATION OU AUCUN CONSEIL ORAL OU ÉCRIT DONNÉ
              PAR OURA OU UN REPRÉSENTANT AUTORISÉ D'OURA NE CONSTITUERA UNE
              GARANTIE. CERTAINES JURIDICTIONS NE PERMETTENT PAS L'EXCLUSION DES
              GARANTIES IMPLICITES OU LES LIMITATIONS DES DROITS STATUTAIRES
              APPLICABLES D'UN CONSOMMATEUR, DE SORTE QUE L'EXCLUSION CI-DESSUS
              PEUT NE PAS S'APPLIQUER.{' '}
            </TypographyV3>
            <TypographyV3>
              NONOBSTANT CE QUI PRÉCÈDE, OURA GARANTIT À L'ACHETEUR UTILISATEUR
              FINAL D'ORIGINE QUE LE PRODUIT EST EXEMPT DE DÉFAUTS, TANT DANS
              LES MATÉRIAUX QUE DANS SA FABRICATION, PENDANT UN (1) AN À COMPTER
              DE LA DATE D'ACHAT. Si la législation locale en vigueur au moment
              de l'achat exige une période de garantie supérieure à un (1) an,
              la présente garantie sera prolongée dans la mesure requise par
              ladite législation. Au cours de la période de garantie, Oura
              réparera ou remplacera sans frais tout composant du Produit qui ne
              respecte pas la garantie limitée prévue. Vous serez redevable des
              frais de transport connexes. Les produits de remplacement peuvent
              être neufs ou reconditionnés, à notre discrétion. Cette garantie
              limitée ne s'applique pas (i) à l'usure normale, y compris les
              rayures et déformations ; (ii) aux composants du Produit, tels que
              les batteries, à moins que les dommages ne soient dus à un défaut
              de matériaux ou de fabrication ; (iii) aux dommages résultant du
              fait que vous n'avez pas utilisé le Produit conformément aux
              instructions accompagnant le Produit Ōura ou disponibles sur le
              site Web ; (iv) aux dommages résultant d'un accident, d'une
              inondation, d'un incendie, d'une mauvaise utilisation ou d'une
              utilisation abusive ; (v) aux dommages résultant d'une quelconque
              intervention, ou aux dommages résultant d'une altération ou d'une
              modification du Produit, par une personne non autorisée par Ōura ;
              ou (vi) à l'utilisation du Produit avec toute autre application ou
              tout autre logiciel que l'Application mobile.
            </TypographyV3>
            <TypographyV3>
              Oura se réserve le droit exclusif de réparer ou de remplacer le
              Produit, ou de proposer un remboursement total, à sa seule
              discrétion. Ce recours sera votre seul et unique recours pour
              toute violation de la présente garantie limitée. Les réparations
              et les remplacements en garantie bénéficient d'une nouvelle
              garantie qui est la plus longue des deux périodes suivantes :
              quatre-vingt-dix (90) jours ou le solde de la garantie originale
              d'un (1) an.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Limitation de responsabilité
            </TypographyV3>
            <TypographyV3>
              EN AUCUN CAS, OURA, SES FILIALES, SES SOCIÉTÉS AFFILIÉES, SES
              DIRIGEANTS, SES ADMINISTRATEURS, SES EMPLOYÉS, SES REPRÉSENTANTS,
              SES AGENTS, SES PARTENAIRES ET SES CONCÉDANTS DE LICENCE NE
              POURRONT ÊTRE TENUS RESPONSABLES DE DOMMAGES DE QUELQUE NATURE QUE
              CE SOIT, EN VERTU DE TOUTE THÉORIE JURIDIQUE, DÉCOULANT DE VOTRE
              UTILISATION OU DE VOTRE INCAPACITÉ À UTILISER LES SERVICES OU TOUT
              CONTENU ASSOCIÉ AUX SERVICES, OU TOUT AUTRE SITE OU TOUT SERVICE
              OU ARTICLE OBTENU PAR LE BIAIS DES SERVICES, Y COMPRIS TOUT
              DOMMAGE DIRECT, INDIRECT, SPÉCIAL, ACCESSOIRE, CONSÉCUTIF OU
              PUNITIF, Y COMPRIS, MAIS SANS S'Y LIMITER, LES DOMMAGES CORPORELS,
              LA DOULEUR ET LA SOUFFRANCE, LA DÉTRESSE ÉMOTIONNELLE, LA PERTE DE
              REVENUS, LA PERTE DE PROFITS, LA PERTE D'AFFAIRES OU D'ÉCONOMIES
              ANTICIPÉES, LA PERTE D'OPPORTUNITÉ COMMERCIALE, L'INTERRUPTION
              D'ACTIVITÉ, LA PERTE D'UTILISATION, LA PERTE DE CLIENTÈLE, LA
              PERTE DE DONNÉES, ET QU'ILS SOIENT OU NON CAUSÉS PAR UN DÉLIT (Y
              COMPRIS LA NÉGLIGENCE), UNE RUPTURE DE CONTRAT OU AUTRE, MÊME
              S'ILS SONT PRÉVISIBLES. CES EXCLUSIONS OU LIMITATIONS
              S'APPLIQUERONT INDÉPENDAMMENT DU FAIT QU'OURA AIT ÉTÉ PRÉVENUE OU
              NON DE LA POSSIBILITÉ DE TELS DOMMAGES.
            </TypographyV3>
            <TypographyV3>
              CE QUI PRÉCÈDE NE SAURAIT AFFECTER LA RESPONSABILITÉ QUI NE PEUT
              ÊTRE EXCLUE OU LIMITÉE EN VERTU DE LA LOI EN VIGUEUR. DANS LA
              MESURE OÙ LA RESPONSABILITÉ NE PEUT ÊTRE EXCLUE OU LIMITÉE COMME
              INDIQUÉ CI-DESSUS, EN AUCUN CAS OURA NE SERA RESPONSABLE DE TOUTE
              RÉCLAMATION, QUE CE SOIT DANS LE CADRE D'UN CONTRAT, D'UN DÉLIT OU
              DE TOUTE AUTRE RESPONSABILITÉ SUPPOSÉE, POUR UN MONTANT SUPÉRIEUR
              À 100 €.
            </TypographyV3>

            <TypographyV3 Element="h2" variant="h3">
              Précautions
            </TypographyV3>
            <TypographyV3>
              LES SERVICES NE SONT PAS DES APPAREILS MÉDICAUX ET VOUS ACCEPTEZ
              EXPRESSÉMENT QUE LES SERVICES N'IMPLIQUENT PAS LA FOURNITURE DE
              CONSEILS MÉDICAUX PAR OURA. LES SERVICES NE CONSTITUENT PAS UN
              MOYEN DE DIAGNOSTIQUER, SOIGNER, GUÉRIR OU PRÉVENIR UNE QUELCONQUE
              MALADIE OU PATHOLOGIE. LES SERVICES NE SONT FOURNIS QU'À TITRE
              INDICATIF ET NE PEUVENT REMPLACER LES SERVICES DE MÉDECINS OU DE
              PROFESSIONNELS DE SANTÉ.
            </TypographyV3>
            <TypographyV3>
              LES SERVICES, Y COMPRIS TOUS LES TEXTES, LES INFORMATIONS, LES
              PHOTOGRAPHIES, LES IMAGES, LES ILLUSTRATIONS, LES GRAPHIQUES, LES
              EXTRAITS AUDIO, VIDÉO ET AUDIO-VIDÉO, ET TOUT AUTRE DOCUMENT,
              QU'ILS SOIENT FOURNIS PAR NOUS OU PAR DES TIERS, NE CONSTITUENT
              PAS ET NE SAURAIENT REMPLACER (a) UN CONSEIL DE VOTRE MÉDECIN OU
              D'AUTRES PROFESSIONNELS DE SANTÉ, (b) UNE VISITE, UN APPEL OU UNE
              CONSULTATION AVEC VOTRE MÉDECIN OU D'AUTRES PROFESSIONNELS DE
              SANTÉ, OU (c) LES INFORMATIONS CONTENUES SUR OU DANS TOUT
              EMBALLAGE OU ÉTIQUETTE DE PRODUIT.
            </TypographyV3>
            <TypographyV3>
              SI VOUS AVEZ DES QUESTIONS DE SANTÉ, VEUILLEZ APPELER OU CONSULTER
              RAPIDEMENT VOTRE MÉDECIN OU UN AUTRE PRESTATAIRE DE SANTÉ. EN CAS
              D'URGENCE, APPELEZ IMMÉDIATEMENT VOTRE MÉDECIN OU COMPOSEZ LE 112.
              VOUS NE DEVEZ JAMAIS IGNORER UN AVIS MÉDICAL OU RETARDER LA
              CONSULTATION D'UN MÉDECIN EN RAISON D'UNE INFORMATION PRÉSENTÉE
              SUR LES SERVICES, ET VOUS NE DEVEZ PAS UTILISER LES SERVICES OU
              TOUTE INFORMATION FOURNIE DANS LES SERVICES POUR DIAGNOSTIQUER OU
              TRAITER UN PROBLÈME DE SANTÉ. LA TRANSMISSION ET LA RÉCEPTION DES
              SERVICES, DANS LEUR INTÉGRALITÉ OU EN PARTIE, OU LA COMMUNICATION
              PAR INTERNET, E-MAIL OU D'AUTRES MOYENS NE CONSTITUENT NI NE
              CRÉENT UNE RELATION MÉDECIN-PATIENT, UNE RELATION
              THÉRAPEUTE-PATIENT OU UNE AUTRE RELATION PROFESSIONNELLE DE SANTÉ
              ENTRE VOUS ET OURA.
            </TypographyV3>
            <TypographyV3>
              Vous devez toujours consulter un médecin avant de modifier vos
              habitudes de sommeil ou votre activité sur la base d'informations
              fournies par les Services, ou si vous avez des questions
              concernant une pathologie. Oura n'est pas responsable des
              problèmes de santé qui pourraient résulter des informations dont
              vous avez pris connaissance par le biais des Services. Si vous
              apportez une quelconque modification à vos habitudes de sommeil ou
              à votre activité en vous appuyant sur les Services, vous convenez
              que vous le faites entièrement à vos propres risques. Vous devez
              prêter une grande attention aux réactions de votre corps. Par
              exemple, si vous ressentez une douleur, une fatigue ou une gêne
              inattendue, répétée ou de longue durée après avoir modifié vos
              habitudes de sommeil ou votre activité, nous vous recommandons de
              consulter un médecin avant de continuer à appliquer ces
              modifications. Les informations fournies par les Services peuvent
              être trompeuses si vos fonctions et réponses physiologiques
              diffèrent sensiblement des moyennes de la population en raison de
              pathologies ou de différences naturelles exceptionnelles.
            </TypographyV3>
            <TypographyV3>
              Veillez à ce que le Produit que vous portez ne s'accroche pas à
              des structures fixes ou à des objets lourds. Si vous constatez une
              rougeur ou une irritation de la peau de votre doigt lorsque vous
              portez le Produit, retirez-le immédiatement. Si les symptômes
              persistent au-delà de deux à trois jours sans utilisation du
              Produit, veuillez contacter un professionnel de santé.
            </TypographyV3>
            <TypographyV3>
              Vous ne devez jamais mettre le Produit Oura dans votre bouche. Le
              Produit Oura n'est pas un jouet et ne doit pas être utilisé par
              des enfants. Vous ne devez jamais laisser un enfant seul avec ce
              Produit puisqu'il existe un risque d'étouffement.
            </TypographyV3>
            <TypographyV3>
              Les Services peuvent indiquer des liens vers d'autres sites Web
              gérés par des tiers. Vous reconnaissez et acceptez que lesdits
              liens ne sont indiqués que pour votre commodité et qu'ils ne
              constituent pas une approbation, une affiliation, une relation ou
              un parrainage de la part d'Oura à l'égard du fournisseur du site
              indiqué en lien ou de la qualité, de la fiabilité ou de toute
              autre caractéristique ou fonctionnalité du site indiqué en lien.
              Vous reconnaissez et acceptez également qu'Oura ne soit en aucun
              cas responsable (y compris, sans s'y limiter, en ce qui concerne
              toute perte ou tout préjudice que vous pourriez subir) de tout
              litige en lien avec le site indiqué en lien, y compris, mais sans
              s'y limiter, le contenu fourni sur un tel site ou par le biais de
              celui-ci, ou l'usage que vous en faites. En outre, vous devez
              savoir que votre utilisation d'un site tiers est soumise aux
              modalités applicables à ce site, y compris les politiques de
              confidentialité (ou l'absence de celles-ci) de ce site. Si un
              tiers fournit un lien vers les Services, cela ne constitue pas
              nécessairement une indication d'approbation, d'affiliation, de
              relation ou de parrainage par ou avec Oura. Il se peut qu'Oura ne
              soit même pas au courant qu'un tiers a fourni un lien vers les
              Services.
            </TypographyV3>
            <TypographyV3>
              Tout autre contenu n'appartenant pas à Oura est la propriété de
              son propriétaire respectif. Vous reconnaissez et acceptez que ce
              contenu est fourni par son propriétaire et qu'il ne constitue pas
              une approbation, une affiliation, une relation ou un parrainage de
              la part d'Oura à l'égard du fournisseur de ce contenu. Vous
              reconnaissez et acceptez également qu'Oura ne soit en aucun cas
              responsable (y compris, mais sans s'y limiter, en ce qui concerne
              toute perte ou tout préjudice que vous pourriez subir) des
              contenus fournis par des tiers y compris, mais sans s'y limiter,
              l'usage que vous en faites. OURA NE FAIT AUCUNE DÉCLARATION ET NE
              FOURNIT AUCUNE GARANTIE QUANT AUX CONTENUS TIERS.
            </TypographyV3>
            <TypographyV3>
              Vous vous engagez à indemniser, à défendre et à dégager Oura, ses
              filiales, sociétés affiliées, dirigeants, administrateurs,
              employés, représentants, agents, partenaires, concédants, ayants
              droit et ayants cause, de toute responsabilité quant aux actions,
              causes, réclamations, dommages, dettes, demandes ou
              responsabilités, y compris les coûts raisonnables et les
              honoraires d'avocat, invoqués par toute personne, découlant de ou
              liés à (i) votre utilisation des Services, y compris, mais sans
              s'y limiter, toute personne utilisant votre compte ou vos
              Identifiants ; (ii) la violation du présent Accord par vous-même
              ou toute personne utilisant votre compte ou vos Identifiants ;
              (iii) toute information utilisée, stockée ou transmise en rapport
              avec votre compte ou vos Identifiants ; (iv) la violation des
              droits d'un tiers, y compris, mais sans s'y limiter, le droit à la
              vie privée, le droit à l'image, les droits de propriété
              intellectuelle ainsi que tous les autres droits de propriété par
              vous-même ou toute personne utilisant votre compte ou vos
              Identifiants ; ou (v) la violation de toute loi, réglementation ou
              autre exigence légale.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Avis pour les utilisateurs résidant en Californie
            </TypographyV3>
            <TypographyV3>
              En vertu de la section 1789.3 du Code civil de Californie, les
              utilisateurs californiens des Services ont droit à l'avis
              spécifique suivant sur les droits des consommateurs:
            </TypographyV3>
            <TypographyV3 className="ml-8">
              Vous pouvez contacter l'équipe d'assistance aux plaintes de la
              division des services aux consommateurs du California Department
              of Consumer Affairs par écrit à l'adresse 1625 N. Market Blvd,
              Suite N 112, Sacramento, California 95834, États-Unis, ou par
              téléphone au (800) 952-5210.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Résiliation ; annulation
            </TypographyV3>
            <TypographyV3>
              Cet Accord restera pleinement en vigueur jusqu'à ce qu'il soit
              résilié ou annulé conformément au présent Accord.
            </TypographyV3>
            <TypographyV3>
              Oura a le droit de résilier le présent Accord (i) pour quelque
              raison que ce soit en vous donnant un préavis de trente (30) jours
              ; (ii) immédiatement en cas de violation substantielle du présent
              Accord de votre part, autre que le non-paiement des Frais ; ou
              (iii) en cas de non-paiement des Frais. Nonobstant ce qui précède,
              Oura se réserve le droit, à sa seule discrétion et sans préavis, à
              tout moment et pour toute raison, de supprimer, modifier,
              suspendre ou désactiver l'accès à une partie ou l'ensemble des
              Services.
            </TypographyV3>
            <TypographyV3>
              Vous pouvez résilier le présent Accord pour quelque raison que ce
              soit en donnant un préavis de trente (30) jours à Oura par e-mail
              à l'adresse support@ouraring.com. Vous serez redevable de tous les
              Frais encourus avant et pendant la période de préavis.
            </TypographyV3>
            <TypographyV3>
              Les sections intitulées Précautions, Droits de propriété
              intellectuelle, Données et communications, Indemnisation,
              Exclusion de garantie, Limitation de responsabilité, Loi
              applicable ; Forum ; Arbitrage obligatoire et exécutoire ;
              Renonciation aux recours collectifs, ainsi que les obligations de
              paiement des Frais encourus avant et pendant toute période de
              préavis restent en vigueur en cas de résiliation du présent Accord
              pour quelque raison que ce soit.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Restrictions d'utilisation applicables au gouvernement fédéral
            </TypographyV3>
            <TypographyV3>
              Si vous êtes un département ou une agence du gouvernement fédéral
              des États-Unis ou si vous passez un contrat au nom d'un tel
              département ou d'une telle agence, les Services sont des articles
              commerciaux (officiellement, « commercial items ») tels que
              définis dans l'article 48 C.F.R. §2.101, constitués de logiciels
              informatiques commerciaux (officiellement, « commercial computer
              software ») et d'une documentation de logiciels informatiques
              commerciaux (officiellement, « commercial computer software
              documentation »), conformément à l'utilisation de ces termes dans
              l'article 48 C.F.R. §12.212 ou 48 C.F.R. §227.7202. Conformément à
              l'article 48 C.F.R. §12.212 ou 48 C.F.R. §227.7202-1 jusqu'à
              227.7202-4, le cas échéant, le Service vous est concédé sous
              licence avec uniquement les droits prévus par les modalités du
              présent Accord. Si vous êtes un commanditaire du gouvernement
              fédéral des États-Unis ou un employé d'un tel commanditaire
              effectuant des achats dans le cadre du programme dit GSA Schedule
              en qualité d'agent de ce commanditaire, les conditions du présent
              Accord publié et de la Politique de confidentialité peuvent
              différer.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Conformité avec la réglementation en matière d'exportation et
              restrictions d'utilisation
            </TypographyV3>
            <TypographyV3>
              Vous n'exporterez ou ne réexporterez pas, directement ou
              indirectement, les Services, ou toute information technique s'y
              rapportant, vers toute destination ou à toute personne interdite
              ou restreinte par la loi applicable, y compris, mais sans s'y
              limiter, l'ensemble des lois et réglementations applicables aux
              États-Unis en matière de limitation des exportations.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Loi applicable ; Forum ; Arbitrage obligatoire et exécutoire ;
              Renonciation aux recours collectifs
            </TypographyV3>
            <TypographyV3>
              Toute action en lien avec le présent Accord, les Services et votre
              relation avec Oura doit être régie et interprétée en vertu des
              lois de l'État de Californie, sans égard à ses règles sur les
              conflits des lois, ET NE SERA EXPRESSÉMENT PAS RÉGIE PAR LES
              CONVENTIONS DES NATIONS UNIES SUR LES CONTRATS DE VENTE
              INTERNATIONALE DE MARCHANDISES, LE CAS ÉCHÉANT. Vous acceptez de
              résoudre tout litige ou toute réclamation découlant du présent
              Accord ou des Services ou s'y rapportant par le biais d'un
              arbitrage définitif et exécutoire par un seul arbitre. Cela inclut
              les litiges découlant de l'interprétation ou de l'application de
              la présente section relative à l'arbitrage obligatoire, ou s'y
              rapportant, y compris son caractère exécutoire, sa révocabilité ou
              sa validité. Nonobstant ce qui précède, l'une ou l'autre des
              parties peut engager une action en justice uniquement pour obtenir
              une injonction afin de mettre fin à l'utilisation non autorisée ou
              à l'utilisation abusive des Services, ou à la violation de toute
              propriété intellectuelle. Sous réserve de la clause d'arbitrage
              obligatoire, les parties conviennent de manière irrévocable
              d'intenter toute action visant à résoudre ou à faire valoir des
              réclamations découlant du présent Accord ou s'y rapportant devant
              les tribunaux fédéraux ou d'État de San Francisco, en Californie,
              États-Unis, et les parties se soumettent de manière irrévocable à
              la compétence exclusive de ces tribunaux dans le cadre de toute
              action, poursuite ou procédure de ce type. Sauf dans la mesure où
              la loi applicable l'interdit, les parties conviennent que toute
              réclamation ou cause d'action découlant de l'utilisation des
              Services ou du présent Accord, ou s'y rapportant, doit être
              déposée dans un délai d'un (1) an après la survenance de ladite
              réclamation ou cause d'action, sous peine de prescription. Ce
              paragraphe ne s'applique pas aux utilisateurs qui résident dans un
              pays de l'Union européenne. Si vous êtes un utilisateur établi
              dans l'Union européenne, le droit finlandais s'appliquera au
              présent Accord et les tribunaux finlandais auront la compétence
              exclusive pour régler tout litige lié au présent Accord. Cette
              disposition ne s'applique pas aux consommateurs des pays qui
              exigent que les accords soient régis par la législation locale du
              pays du consommateur. La version originale en langue anglaise du
              présent Accord prévaut sur tous les documents, les avis et les
              interprétations du présent Accord. Vous acceptez également de
              renoncer à tout droit de faire valoir toute réclamation contre
              Oura en tant que représentant ou membre d'une action collective ou
              représentative, sauf si une telle renonciation est interdite par
              la loi ou considérée par un tribunal comme étant contraire à
              l'ordre public.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Divers
            </TypographyV3>
            <TypographyV3>
              Vous acceptez qu'Oura ait le droit de surveiller l'utilisation des
              Services afin de garantir la conformité avec l'Accord.
            </TypographyV3>
            <TypographyV3>
              La renonciation à une modalité, condition ou disposition du
              présent Accord, que ce soit par le comportement ou de toute autre
              manière, dans un ou plusieurs cas, ne doit pas être considérée
              comme, et ne constitue pas, une renonciation à une autre modalité,
              condition ou disposition des présentes, similaire ou non, et une
              telle renonciation ne constitue pas une renonciation permanente à
              une modalité, disposition ou condition des présentes. Aucune
              renonciation n'est contraignante sauf si la partie atteste par
              écrit sa renonciation.
            </TypographyV3>
            <TypographyV3>
              Vous ne pouvez pas céder le présent Accord à une autre partie, et
              toute tentative en ce sens sera considérée comme nulle et non
              avenue.
            </TypographyV3>
            <TypographyV3>
              Si l'une des dispositions du présent Accord est jugée illégale ou
              inapplicable, cette disposition sera appliquée dans toute la
              mesure du possible, et les autres dispositions resteront
              entièrement en vigueur et applicables.
            </TypographyV3>
            <TypographyV3>
              Le présent Accord et la Politique de confidentialité constituent
              la déclaration complète et exclusive de l'accord entre vous et
              Oura concernant les Services, et remplacent l'ensemble des
              communications, déclarations et accords antérieurs ou
              contemporains, verbaux ou écrits, entre les parties.
            </TypographyV3>
            <TypographyV3>
              En cas de conflit entre les conditions du présent Accord et les
              conditions de la Politique de confidentialité, les conditions du
              présent Accord prévalent.
            </TypographyV3>
            <TypographyV3 Element="h2" variant="h3">
              Modification des modalités et des Services
            </TypographyV3>
            <TypographyV3>
              Oura se réserve le droit de mettre à jour le présent Accord et/ou
              la Politique de confidentialité à tout moment pour quelque motif
              que ce soit, à sa seule discrétion, en publiant les conditions
              mises à jour. Sauf indication contraire d'Oura, toute modification
              entrera en vigueur de manière prospective à partir de la date de
              publication. Oura vous informera de toute modification
              substantielle de l'Accord ou des Services. En continuant à accéder
              aux Services ou à les utiliser après que nous vous ayons informé
              d'une modification, vous acceptez d'être lié par l'Accord modifié.
              Dans le cas où l'Accord modifié ne serait pas acceptable pour
              vous, votre seul recours est de mettre fin à votre utilisation des
              Services. Oura et ses fournisseurs de services tiers sont
              susceptibles d'apporter des améliorations et/ou des modifications
              aux Services, fonctionnalités et prix décrits, à tout moment, pour
              quelque motif que ce soit et à leur seule discrétion.
              L'Application mobile peut télécharger et installer des mises à
              niveau, des mises à jour et d'autres fonctionnalités dans le but
              d'améliorer et de développer les Services. Oura se réserve le
              droit, à tout moment, de modifier ou d'interrompre, temporairement
              ou définitivement, les Services ou toute partie de ceux-ci avec ou
              sans préavis. Vous acceptez qu'Oura n'ait aucune responsabilité à
              votre égard ou à celui d'un tiers pour toute modification, toute
              suspension ou tout arrêt des Services.
            </TypographyV3>
            {enable2024Terms ? (
              <TermsOfDeliverySection />
            ) : (
              <>
                <TypographyV3 Element="h1" variant="h1">
                  CONDITIONS DE LIVRAISON D'ŌURA
                </TypographyV3>
                <TypographyV3>
                  <em>Dernière mise à jour : le 18 octobre 2021</em>
                </TypographyV3>
                <TypographyV3>
                  L'entité juridique d'Oura est la Finlande. Cela peut signifier
                  que votre paiement peut être sujet à une transaction
                  internationale en cas d'utilisation d'une carte de crédit.
                  Veuillez contacter la banque émettrice de votre carte pour en
                  savoir plus. Les anneaux sont envoyés depuis l'Union
                  européenne. Oura effectue ses livraisons conformément aux
                  règles Incoterms® 2020 comme indiqué ci-dessous.
                </TypographyV3>
                <TypographyV3 Element="h2" variant="h3">
                  Au sein de l'Union européenne et aux États-Unis
                </TypographyV3>
                <TypographyV3>RDA – Rendus droits acquittés</TypographyV3>
                <TypographyV3>
                  Oura est responsable de la livraison des biens à l'adresse
                  indiquée dans le pays de l'acheteur et prend en charge tous
                  les frais afférents à la livraison des biens à destination, y
                  compris les droits et taxes d'importation.
                </TypographyV3>
                <TypographyV3 Element="h2" variant="h3">
                  En dehors de l'Union européenne et des États-Unis
                </TypographyV3>
                <TypographyV3>
                  RLD – Rendu au lieu de destination e
                </TypographyV3>
                <TypographyV3>
                  Oura paie le transport jusqu'à l'adresse indiquée, à
                  l'exception des frais afférents au dédouanement à
                  l'importation, et assume tous les risques avant le moment où
                  les biens sont livrés à l'acheteur.
                </TypographyV3>
              </>
            )}
          </>
          <TypographyV3 className="mt-40">
            {t('legal_disclaimer_translations')}
          </TypographyV3>
        </TypographyRhythm>
      </LayoutGrid>
    </PageLayoutV3>
  );
};

Page.pageName = 'Terms and Conditions';
Page.isSormusCompatible = true;

export default Page;
